import React, { useState, useEffect, useRef } from 'react'
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import { ChevronUpDownIcon } from '@heroicons/react/16/solid'
import { CheckIcon } from '@heroicons/react/20/solid'
import api from '../../../utils/apiClient'

const timePeriodOptions = [
	{ id: '24h', name: 'Last 24 Hours' },
	{ id: '7d', name: 'Last 7 Days' },
]

function BrokerEmailsModal({ isOpen, onClose, broker, timePeriod, selectedAccount }) {
	const [emails, setEmails] = useState([])
	const [loading, setLoading] = useState(false)
	const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 })
	const [period, setPeriod] = useState(timePeriod)
	const modalRef = useRef(null)

	useEffect(() => {
		if (isOpen) {
			setPeriod(timePeriod)
		}
	}, [isOpen, timePeriod])

	useEffect(() => {
		if (isOpen && broker?.mcNumber) {
			fetchEmails(1)
		}
	}, [isOpen, broker?.mcNumber, period, selectedAccount])

	useEffect(() => {
		function handleClickOutside(event) {
			if (modalRef.current && !modalRef.current.contains(event.target)) {
				onClose()
			}
		}

		function handleEscape(event) {
			if (event.key === 'Escape') {
				onClose()
			}
		}

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside)
			document.addEventListener('keydown', handleEscape)
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
			document.removeEventListener('keydown', handleEscape)
		}
	}, [isOpen, onClose])

	async function fetchEmails(page) {
		setLoading(true)
		try {
			const params = new URLSearchParams({
				period: period,
				page: page.toString(),
				limit: '20',
			})
			if (selectedAccount && selectedAccount !== 'all') {
				params.append('account', selectedAccount)
			}

			const response = await api.get(`/api/emails/broker/${broker.mcNumber}?${params}`)
			setEmails(response.data.emails)
			setPagination(response.data.pagination)
		} catch (error) {
			console.error('Failed to fetch broker emails:', error)
		} finally {
			setLoading(false)
		}
	}

	function formatDate(dateString) {
		const date = new Date(dateString)
		const now = new Date()
		const diffMs = now - date
		const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
		const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

		if (diffHours < 24) {
			return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
		} else if (diffDays < 7) {
			return date.toLocaleDateString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit' })
		}
		return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
	}

	if (!isOpen) return null

	const totalOpens = emails.reduce((sum, e) => sum + (e.openCount || 0), 0)
	const totalReplies = emails.reduce((sum, e) => sum + (e.replyCount || 0), 0)

	return (
		<div className="relative z-10">
			<div className="fixed inset-0 bg-black/50 transition-opacity duration-300" />
			<div className="fixed inset-0 z-10 w-screen overflow-y-auto">
				<div className="flex min-h-full items-start justify-center pt-12 px-4 pb-4">
				<div
					ref={modalRef}
					className="relative w-full max-w-2xl bg-[#15202b] rounded-xl shadow-[0_0_20px_rgba(59,130,246,0.15)] ring-1 ring-white/10 overflow-hidden"
				>
					<div className="p-5 border-b border-gray-700/50">
						<div className="flex items-center justify-between">
							<div>
								<h2 className="text-lg font-medium text-white">{broker?.name || 'Unknown Broker'}</h2>
								<p className="text-gray-500 text-sm mt-0.5">MC# {broker?.mcNumber}</p>
							</div>
							<button
								onClick={onClose}
								className="text-gray-500 hover:text-white transition-colors p-1"
							>
								<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
								</svg>
							</button>
						</div>

						{!loading && (
							<div className="flex items-center gap-6 mt-4 text-sm">
								<span className="text-gray-400">
									<span className="text-white font-medium">{pagination.total}</span> sent
								</span>
								<span className="text-gray-400">
									<span className="text-white font-medium">{broker?.uniqueRecipients || 0}</span> recipients
								</span>
								{totalOpens > 0 && (
									<span className="text-gray-400">
										<span className="text-green-400 font-medium">{totalOpens}</span> opens
									</span>
								)}
								{totalReplies > 0 && (
									<span className="text-gray-400">
										<span className="text-blue-400 font-medium">{totalReplies}</span> replies
									</span>
								)}
								<Listbox value={timePeriodOptions.find(o => o.id === period)} onChange={option => setPeriod(option.id)}>
									<div className="relative ml-auto">
										<ListboxButton className="flex items-center gap-1 rounded-md bg-white/5 py-1 pl-2.5 pr-1.5 text-xs text-gray-300 outline-1 -outline-offset-1 outline-white/10 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-blue-500">
											<span>{timePeriodOptions.find(o => o.id === period)?.name}</span>
											<ChevronUpDownIcon className="w-4 h-4 text-gray-400" />
										</ListboxButton>
										<ListboxOptions className="absolute right-0 z-20 mt-1 overflow-auto rounded bg-gray-800 py-0.5 text-[11px] border border-white/10 shadow-lg">
											{timePeriodOptions.map(option => (
												<ListboxOption
													key={option.id}
													value={option}
													className="group cursor-pointer py-1 px-2 text-gray-300 select-none data-[focus]:bg-blue-500 data-[focus]:text-white"
												>
													<span>{option.name}</span>
												</ListboxOption>
											))}
										</ListboxOptions>
									</div>
								</Listbox>
							</div>
						)}
					</div>

					<div className="max-h-[45vh] overflow-y-auto">
						{loading ? (
							<div className="flex items-center justify-center py-16">
								<div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-600 border-t-blue-500" />
							</div>
						) : emails.length === 0 ? (
							<div className="text-center py-16">
								<svg className="w-12 h-12 text-gray-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
								</svg>
								<p className="text-gray-500">No emails found</p>
							</div>
						) : (
							<div className="divide-y divide-gray-700/50">
								{emails.map(email => (
									<div
										key={email._id}
										className="px-5 py-4 hover:bg-white/[0.02] transition-colors"
									>
										<div className="flex items-start gap-3">
											<div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
												email.replyCount > 0 ? 'bg-blue-400' : email.openCount > 0 ? 'bg-green-400' : 'bg-gray-600'
											}`} />
											<div className="flex-1 min-w-0">
												<div className="flex items-start justify-between gap-3">
													<h4 className="text-white font-medium truncate text-sm">{email.subject}</h4>
													<span className="text-gray-500 text-xs flex-shrink-0">{formatDate(email.createdAt)}</span>
												</div>
												<p className="text-gray-500 text-xs mt-1 truncate">From: {email.from}</p>
												<p className="text-gray-500 text-xs truncate">To: {email.to}</p>
												<div className="flex items-center gap-3 mt-2">
													{email.openCount > 0 && (
														<span className="inline-flex items-center gap-1 text-xs text-green-400/80">
															<svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
															</svg>
															{email.openCount}
														</span>
													)}
													{email.replyCount > 0 && (
														<span className="inline-flex items-center gap-1 text-xs text-blue-400/80">
															<svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
															</svg>
															{email.replyCount}
														</span>
													)}
												</div>
											</div>
										</div>
									</div>
								))}
							</div>
						)}
					</div>

					{pagination.totalPages > 1 && (
						<div className="flex items-center justify-center gap-2 px-5 py-4 border-t border-gray-700/50 bg-gray-800/30">
							<button
								onClick={() => fetchEmails(pagination.page - 1)}
								disabled={pagination.page <= 1}
								className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed transition-all"
							>
								<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
								</svg>
							</button>
							<div className="flex items-center gap-1">
								{Array.from({ length: Math.min(pagination.totalPages, 5) }, (_, i) => {
									let pageNum
									if (pagination.totalPages <= 5) {
										pageNum = i + 1
									} else if (pagination.page <= 3) {
										pageNum = i + 1
									} else if (pagination.page >= pagination.totalPages - 2) {
										pageNum = pagination.totalPages - 4 + i
									} else {
										pageNum = pagination.page - 2 + i
									}
									return (
										<button
											key={pageNum}
											onClick={() => fetchEmails(pageNum)}
											className={`w-8 h-8 rounded-lg text-sm font-medium transition-all ${
												pagination.page === pageNum
													? 'bg-blue-500 text-white'
													: 'text-gray-400 hover:text-white hover:bg-white/10'
											}`}
										>
											{pageNum}
										</button>
									)
								})}
							</div>
							<button
								onClick={() => fetchEmails(pagination.page + 1)}
								disabled={pagination.page >= pagination.totalPages}
								className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed transition-all"
							>
								<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
								</svg>
							</button>
						</div>
					)}
				</div>
			</div>
			</div>
		</div>
	)
}

export default BrokerEmailsModal
